import { useQuery, useQueries } from 'react-query';
import { useMemo } from 'react';
import NaurService from '../../services/naurService';
import useAbortController from '../useAbortController';

const naurService = new NaurService();

/**
 * Custom hook to fetch NAUR events for a specific building
 * Returns events with unit details for dropdown display
 * Uses React Query for caching and state management
 *
 * @param {string} buildingId - The building ID to fetch NAUR events for
 * @param {boolean} enabled - Whether to enable the query
 * @returns React Query result with building NAUR events data
 */
export const useNaurEventsForBuilding = (
  buildingId: ?string,
  enabled: boolean = true,
) => {
  const abortControllerOptions = useAbortController();

  return useQuery(
    ['naurEvents', 'building', buildingId],
    async () => {
      if (!buildingId) {
        throw new Error('Building ID is required');
      }
      return await naurService.getNaurEventsForBuilding(
        buildingId,
        abortControllerOptions,
      );
    },
    {
      enabled: !!buildingId && enabled, // Only fetch if buildingId exists and enabled
    },
  );
};

/**
 * Custom hook to fetch NAUR events for multiple buildings in parallel.
 *
 * Only fetches when the user has explicitly selected one or more buildings.
 * When `selectedBuildingIds` is empty, returns an empty list immediately so
 * the caller can display the "select a building" prompt.
 *
 * @param {string[]} selectedBuildingIds - IDs of buildings the user has selected.
 * @returns {{ naurEvents: NaurEventDetails[], isLoading: boolean, isError: boolean }}
 */
export const useNaurEventsDetailsForBuildings = (
  selectedBuildingIds: string[],
) => {
  const abortControllerOptions = useAbortController();

  // Only query for the explicitly selected buildings.
  const queries = useQueries(
    selectedBuildingIds.map((buildingId) => ({
      queryKey: ['naurEvents', 'building', buildingId, 'details'],
      queryFn: async () => {
        return await naurService.getNaurEventsDetailsForBuilding(
          buildingId,
          abortControllerOptions,
        );
      },
      enabled: !!buildingId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    })),
  );

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);

  // Flatten all building results into one deduplicated array keyed on event id.
  const naurEvents = useMemo(() => {
    const seen = new Set();
    return queries
      .flatMap((q) => q.data?.naurEvents ?? [])
      .filter((event) => {
        if (seen.has(event.id)) return false;
        seen.add(event.id);
        return true;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries.map((q) => q.data)]);

  return { naurEvents, isLoading, isError };
};
